import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import type { DocumentResource } from '@getaccept/lib-shared/src/resources/types/document-resource';
import type { Resource } from '@getaccept/lib-shared/src/resources/types/resource';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import type { ImplementResourceInDocumentDTO } from '../dtos/implement-resource-in-document-dto';
import type { ImplementResourceDTO } from '../dtos/implement-resource-dto';
import type { CreateResourceDTO } from '../dtos/create-resource-dto';
import type { UpdateResourceDTO } from '../dtos/update-resource-dto';
import { ResourceMutations } from '../graphql/resource.mutations';
import { ResourceQueries } from '../graphql/resource.queries';
import type { ResourcesDto } from '../dtos/resources-dto';

export class ResourceService {
  static async loadList(
    resourcesDto: ResourcesDto
  ): Promise<{ resources: Resource[]; totalCount: number }> {
    const { resourceType, ...rest } = resourcesDto;
    const {
      data: {
        resources: { resources, totalCount },
      },
    } = await apolloClientInternal.query({
      query: ResourceQueries.loadResourcesQuery,
      variables: {
        ...rest,
        resourceType: resourceType || undefined,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return { resources, totalCount };
  }

  static async loadDetails(id: string): Promise<Resource> {
    const {
      data: { resource },
    } = await apolloClientInternal.query({
      query: ResourceQueries.loadResourceQuery,
      variables: { id },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return resource;
  }

  static async create(payload: CreateResourceDTO): Promise<Resource> {
    const {
      data: { createResource },
    } = await apolloClientInternal.mutate({
      mutation: ResourceMutations.createResourceMutation,
      variables: { payload },
    });
    return createResource;
  }

  static async update(payload: UpdateResourceDTO): Promise<Resource> {
    const {
      data: { updateResource },
    } = await apolloClientInternal.mutate({
      mutation: ResourceMutations.updateResourceMutation,
      variables: { payload },
    });
    return updateResource;
  }

  static async delete(id: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ResourceMutations.deleteResourceMutation,
      variables: { id },
    });
  }

  static async deleteMany(ids: string[]): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ResourceMutations.deleteResourcesMutation,
      variables: { ids },
    });
  }

  static async implementInDocument(payload: ImplementResourceInDocumentDTO): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ResourceMutations.implementResourceInDocumentMutation,
      variables: { payload },
    });
  }

  static async implementInTemplate(payload: ImplementResourceDTO): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ResourceMutations.implementResourceInTemplateMutation,
      variables: { payload },
    });
  }

  static async loadDocumentResources({
    documentId,
    resourceId,
    contentId,
    inTemplate,
  }: {
    documentId?: string;
    resourceId?: string;
    contentId?: string;
    inTemplate?: boolean;
  }): Promise<DocumentResource[]> {
    const {
      data: { documentResources },
    } = await apolloClientInternal.query({
      query: ResourceQueries.documentResourcesQuery,
      variables: { documentId, resourceId, contentId, inTemplate },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return documentResources;
  }

  static async disconnect(payload: ImplementResourceDTO): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ResourceMutations.disconnectResourceMutation,
      variables: { payload },
    });
  }

  static async createResourceDirectory(title: string, directory: string): Promise<Resource> {
    const {
      data: { createResourceDirectory },
    } = await apolloClientInternal.mutate({
      mutation: ResourceMutations.createResourceDirectory,
      variables: { payload: { title, directory } },
    });
    return createResourceDirectory;
  }

  static async moveResources(ids: string[], directory: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ResourceMutations.moveResourcesMutation,
      variables: {
        ids,
        directory,
      },
    });
  }
}
